import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Global } from "@solar-icons/react"
import { useLocale } from "@/hooks/use-locale"
import { LOCALE_OPTIONS } from "@/i18n/locale-options"
import type { SupportedLocale } from "@/i18n/locales"
import { useAuthStore } from "@/stores/auth"
import { cn } from "@/lib/utils"

type LanguageSwitcherProps = {
  className?: string
  onSelect?: () => void
  variant?: "icon" | "full"
  buttonVariant?: "ghost" | "secondary" | "outline"
}

export function LanguageSwitcher({
  className,
  onSelect,
  variant = "icon",
  buttonVariant = "ghost",
}: LanguageSwitcherProps) {
  const { t } = useTranslation("settings")
  const { locale, changeLocale, isChanging } = useLocale()
  const token = useAuthStore((state) => state.token)

  const handleSelect = async (nextLocale: SupportedLocale) => {
    if (nextLocale === locale || isChanging) return
    await changeLocale(nextLocale, { syncProfile: !!token })
    onSelect?.()
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={buttonVariant}
          size={variant === "icon" ? "icon" : "sm"}
          className={cn(
            variant === "icon"
              ? "h-9 w-9 text-muted-foreground hover:text-foreground"
              : "gap-2",
            className,
          )}
          aria-label={t("language.title")}
          disabled={isChanging}
        >
          <Global className="h-5 w-5" />
          {variant === "full" && (
            <span>
              {t(LOCALE_OPTIONS.find((o) => o.value === locale)?.labelKey ?? "language.english")}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LOCALE_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={cn(locale === option.value && "bg-accent")}
          >
            {t(option.labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
